import { type FC, type PropsWithChildren, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { LoadingSpinner } from "widgets";
import { toSafeInt } from "widgets/utils";

import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import ContigGenomeView from "./ContigGenomeView.js";
import ContigSummaryView from "./ContigSummaryView.js";
import { isGenomeViewerEnabledEntity } from "@/app-utils/entity.ts";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { type IContigState, shouldLoadContig } from "@/redux/modules/contig.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";
import ContigRelatedEntitiesView from "@/ui-modules/entities/ContigRelatedEntitiesView.tsx";
import { ContigNucleotideFeatureSelector } from "@/ui-modules/entities/NucleotideFeatureSelector.tsx";
import { ContigRelatedGenomicFeaturesView } from "@/ui-modules/entities/RelatedGenomicFeaturesView.tsx";

/**
 * The ContigPage smart component is called from the route corresponding to contig/:contigId.
 * The children prop corresponds to the smart component injected from the nested routes (e.g. contig/:contigId/summary)
 * The ContigPage will take care of filling up the basic contig data and the nested child pages may request additional
 * data that is needed to show the nested page (such as contig and contig child collections for the summary page)
 */
const ContigDetailPage: PropsWithChildren<FC> = () => {
  const { contigId } = useParams<"contigId">();
  const { contig, isLoading } = useSelector<IAppState, IContigState>((state) => state.contig);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadContig, contigId);

  useEffect(() => {
    void load();
  }, [load]);

  const genomeViewerDisabled = !isGenomeViewerEnabledEntity(contig?.organism);
  const genomeViewDec = {
    disabled: isLoading || genomeViewerDisabled,
    tooltip: genomeViewerDisabled ? "Only features on genomic sequences can be loaded to the Genome Viewer." : null,
  };

  const genesDisabled = !contig?.contigStatistics.geneCount;
  const transcriptsDisabled = !contig?.contigStatistics.transcriptsCount;
  const proteinsDisabled = !contig?.contigStatistics.proteinsCount;
  const genomicFeaturesDisabled =
    !contig?.contigStatistics.nuFeaturesCount.length && !contig?.contigStatistics.markersCount.length;

  const navItems: IRouteNavItemProps[] = [
    {
      label: "Summary",
      route: "summary",
      pageComponent: <ContigSummaryView contigId={toSafeInt(contigId)} />,
      default: true,
      isLoading,
    },
    {
      label: "Genes",
      disabled: genesDisabled,
      disabledTooltip: getTooltip("genes", genesDisabled),
      route: "genes",
      pageComponent: <ContigRelatedEntitiesView subtitle="List of Genes" relatedEntitiesType={ENTITY_TYPE.GENE} />,
      isLoading,
    },
    {
      label: "Transcripts",
      disabled: transcriptsDisabled,
      disabledTooltip: getTooltip("transcripts", transcriptsDisabled),
      route: "transcripts",
      pageComponent: (
        <ContigRelatedEntitiesView subtitle="List of Transcripts" relatedEntitiesType={ENTITY_TYPE.TRANSCRIPT} />
      ),
      isLoading,
    },
    {
      label: "Proteins",
      disabled: proteinsDisabled,
      disabledTooltip: getTooltip("proteins", proteinsDisabled),
      route: "proteins",
      pageComponent: (
        <ContigRelatedEntitiesView subtitle="List of Proteins" relatedEntitiesType={ENTITY_TYPE.PROTEIN} />
      ),
      isLoading,
    },
    {
      label: "Genomic Features",
      disabled: genomicFeaturesDisabled,
      disabledTooltip: getTooltip("genomic features", genomicFeaturesDisabled),
      route: "features",
      pageComponent: (
        <ContigRelatedGenomicFeaturesView toolbarElement={<ContigNucleotideFeatureSelector contig={contig} />} />
      ),
      isLoading,
    },
    {
      label: "Genome View",
      route: "genome",
      pageComponent: <ContigGenomeView />,
      disabled: genomeViewDec.disabled,
      disabledTooltip: genomeViewDec.tooltip,
      isLoading,
    },
  ];

  const getNavItems = (): IRouteNavItemProps[] => {
    return contig?.organism?.id ? navItems : navItems.filter((n) => n.route !== "/genome");
  };

  if (isLoading) {
    return <LoadingSpinner inCenter />;
  }
  if (!contig) {
    return (
      <div key="placeholder">
        <ContentPlaceholder text={"There is no such contig"} />
      </div>
    );
  }

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.CONTIG}
      entityId={toSafeInt(contigId)}
      getDisplayName={() => contig?.sequenceId}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export default ContigDetailPage;

function getTooltip(entity: string, disabled: boolean) {
  return disabled ? `No ${entity} are available for this contig` : null;
}
